import React, { useEffect, useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, formatTL } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, Building2, Lock, Landmark } from "lucide-react";

export default function Checkout() {
  const { items, clear } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [priced, setPriced] = useState({ items: [], total: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    shipping_address: user?.address || "",
    phone: user?.phone || "",
    notes: "",
    payment_method: "stripe",
  });

  useEffect(() => {
    if (user) setForm(f => ({ ...f, shipping_address: f.shipping_address || user.address || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  useEffect(() => {
    if (items.length === 0) return;
    api.post("/cart/price", items).then(r => setPriced(r.data));
  }, [items, user]);

  const isB2B = user?.role === "b2b_dealer" && user?.b2b_status === "approved";
  const allowed = priced.allowed_payment_methods || ["stripe"];
  const cariShort = form.payment_method === "cari" && priced.total > (priced.cari_available || 0);

  if (loading) return <div className="kz-container py-20 text-center text-ink-500">Yükleniyor...</div>;
  if (!user) return <Navigate to="/giris?next=/odeme" replace/>;

  if (items.length === 0) {
    return (
      <div className="kz-container py-20 text-center">
        <h1 className="font-display text-2xl font-black mb-3">Sepetiniz boş</h1>
        <Link to="/katalog" className="kz-btn-primary">Kataloğa Git</Link>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!form.shipping_address || !form.phone) { toast.error("Lütfen tüm alanları doldurun"); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        items: items.map(i => ({ product_id: i.product_id, name: i.name, sku: i.sku, quantity: i.quantity, unit_price: i.unit_price, image_url: i.image_url })),
        shipping_address: form.shipping_address,
        phone: form.phone,
        notes: form.notes,
        payment_method: form.payment_method,
        origin_url: window.location.origin,
      });
      if (r.data.checkout_url) {
        clear();
        window.location.href = r.data.checkout_url;
      } else {
        clear();
        toast.success("Siparişiniz oluşturuldu");
        navigate("/hesabim");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sipariş oluşturulamadı");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="kz-container py-10">
      <h1 className="font-display text-3xl md:text-4xl font-black mb-8">Sipariş Tamamla</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-6">
          <section className="kz-card p-6">
            <h2 className="font-display font-black mb-4 pb-3 border-b border-ink-200">TESLİMAT BİLGİLERİ</h2>
            <label className="block mb-3">
              <span className="text-sm font-semibold mb-1 block">Teslimat Adresi</span>
              <textarea data-testid="checkout-address" required rows={3} value={form.shipping_address}
                onChange={(e)=>setForm({...form, shipping_address: e.target.value})}
                className="w-full border border-ink-200 px-3 py-2 outline-none focus:border-ink-900"/>
            </label>
            <label className="block mb-3">
              <span className="text-sm font-semibold mb-1 block">Telefon</span>
              <input data-testid="checkout-phone" required value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})}
                placeholder="0 (5__) ___ __ __"
                className="w-full border border-ink-200 px-3 py-2 outline-none focus:border-ink-900"/>
            </label>
            <label className="block">
              <span className="text-sm font-semibold mb-1 block">Sipariş Notu (Opsiyonel)</span>
              <textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}
                className="w-full border border-ink-200 px-3 py-2 outline-none focus:border-ink-900"/>
            </label>
          </section>

          <section className="kz-card p-6">
            <h2 className="font-display font-black mb-4 pb-3 border-b border-ink-200">ÖDEME YÖNTEMİ</h2>
            <div className="space-y-3">
              <label className={`flex items-start gap-3 p-4 border cursor-pointer ${form.payment_method === "stripe" ? "border-ink-900 bg-ink-100" : "border-ink-200"}`}>
                <input data-testid="payment-stripe" type="radio" checked={form.payment_method === "stripe"} onChange={()=>setForm({...form, payment_method: "stripe"})}/>
                <CreditCard size={20} className="text-brand"/>
                <div>
                  <div className="font-bold">Kredi / Banka Kartı</div>
                  <div className="text-xs text-ink-500">Stripe güvenli ödeme altyapısı</div>
                </div>
              </label>
              {isB2B && allowed.includes("havale") && (
                <label className={`flex items-start gap-3 p-4 border cursor-pointer ${form.payment_method === "havale" ? "border-ink-900 bg-ink-100" : "border-ink-200"}`}>
                  <input data-testid="payment-havale" type="radio" checked={form.payment_method === "havale"} onChange={()=>setForm({...form, payment_method: "havale"})}/>
                  <Landmark size={20} className="text-ink-700"/>
                  <div>
                    <div className="font-bold">Havale / EFT</div>
                    <div className="text-xs text-ink-500">Sipariş sonrası banka bilgileri iletilir</div>
                  </div>
                </label>
              )}
              {isB2B && allowed.includes("cari") && (
                <label className={`flex items-start gap-3 p-4 border cursor-pointer ${form.payment_method === "cari" ? "border-b2b bg-b2b/10" : "border-ink-200"}`}>
                  <input data-testid="payment-cari" type="radio" checked={form.payment_method === "cari"} onChange={()=>setForm({...form, payment_method: "cari"})}/>
                  <Building2 size={20} className="text-b2b"/>
                  <div>
                    <div className="font-bold">Cari Hesap (Vadeli)</div>
                    <div className="text-xs text-ink-500">Bakiye: {formatTL(user.credit_balance)} · Kullanılabilir limit: <b data-testid="checkout-cari-available">{formatTL(priced.cari_available)}</b></div>
                  </div>
                </label>
              )}
              {cariShort && (
                <div data-testid="checkout-cari-insufficient" className="text-sm bg-red-50 border border-red-200 text-red-700 px-3 py-2">
                  Cari limitiniz bu sipariş için yetersiz. Lütfen başka bir ödeme yöntemi seçin veya yöneticinizle iletişime geçin.
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="kz-card p-6 h-fit lg:sticky lg:top-24">
          <div className="font-display font-black text-lg mb-4 pb-4 border-b border-ink-200">SİPARİŞ ÖZETİ</div>
          <div className="space-y-2 mb-4 max-h-60 overflow-auto">
            {priced.items.map(i => (
              <div key={i.product_id} className="flex justify-between text-sm">
                <span className="truncate pr-2">{i.quantity}x {i.name}</span>
                <span className="font-semibold shrink-0">{formatTL(i.unit_price * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-lg font-display font-black border-t border-ink-200 pt-3">
            <span>Toplam</span>
            <span>{formatTL(priced.total)}</span>
          </div>
          <button data-testid="place-order-btn" type="submit" disabled={submitting || cariShort} className="kz-btn-primary w-full mt-6">
            <Lock size={16}/> {submitting ? "İşleniyor..." : form.payment_method === "stripe" ? "Güvenli Öde" : "Siparişi Onayla"}
          </button>
        </aside>
      </form>
    </div>
  );
}
